import type { ReactNode } from 'react';
import { BrowserLocaleSource, LocaleSwitcher, StoredLocaleSource, resolveSupportedLocale } from '@talbenmoshe/prometheus-i18n-runtime';
import type { ILocaleController, ILocaleSwitcher, LocaleBundles } from '@talbenmoshe/prometheus-i18n-runtime';
import { ReactTranslator } from '@talbenmoshe/prometheus-i18n-react';
import { Messages, SUPPORTED_LOCALES, loadMessages } from '@talbenmoshe/trip-os-i18n';
import type { IMessages } from '@talbenmoshe/trip-os-i18n';

const FALLBACK_LOCALE = 'en';
const LOCALE_STORAGE_KEY = 'trip-os-users-web.locale';

export interface AppTranslation {
  readonly messages: IMessages<ReactNode>;
  readonly localeController: ILocaleController;
  readonly localeSwitcher: ILocaleSwitcher;
}

async function loadBundles(locale: string): Promise<LocaleBundles> {
  return { messages: await loadMessages(locale) };
}

/**
 * Composes translation for this app: a remembered choice wins over the browser language, a locale
 * the package does not ship falls back to English, and the switcher is the only way to change it.
 */
export async function createAppTranslator(): Promise<AppTranslation> {
  const localeSource = new StoredLocaleSource(
    window.localStorage,
    new BrowserLocaleSource(window.navigator, FALLBACK_LOCALE),
    LOCALE_STORAGE_KEY
  );

  const locale = resolveSupportedLocale(await localeSource.getLocale(), SUPPORTED_LOCALES) ?? FALLBACK_LOCALE;
  const translator = await ReactTranslator.create({ locale, fallbackLocale: FALLBACK_LOCALE, bundles: await loadBundles(locale) });

  return {
    messages: new Messages<ReactNode>(translator),
    localeController: translator,
    localeSwitcher: new LocaleSwitcher(translator, loadBundles, localeSource, SUPPORTED_LOCALES)
  };
}
